import React, {useEffect, useMemo} from "react";
import { useDispatch, useSelector } from "react-redux";
import { CartesianGrid, Legend, LineChart, XAxis, YAxis, Line, ResponsiveContainer, Tooltip, Brush } from "recharts";
import RootState from "../../../redux/states/root.state";
import { AppDispatch } from "../../../redux/store";
import { fetchSpaHistory } from "../../../redux/actions/spaHistory.actions";
import {clearSpaHistory} from "../../../redux/reducers/spaHistory.reducer";
import ReportLoadingProgress from "./ReportLoadingProgress";
import { useReportTimeFormat } from "../reportTime";
import useIsDarkTheme from "../../../hooks/useIsDarkTheme";
import { palName } from "../../../utils/brand";
import { PumpState } from "../reportEnums.enums";

// The live keys this graph draws from: electrode current and voltage, Pump1, ORP and pH.
export const V1_SPABOY_GRAPH_FIELDS = [`sbI1`,`sbVout`,`P1`,`sbORP`,`sbpH`];

// The classic portal's Spa Boy chart (UC_SpaBoyChart), line for line: everything on one
// shared axis, current and voltage scaled down to sit beside ORP and pH, and the pump as
// a step between two fixed levels. pH stays x100, as classic plots it. Classic's
// SpaInteraction line is left out: on the MySQL stack it never had data and drew flat.
const SERIES = [
  {key: 'current', label: 'Current(1/3)'},
  {key: 'voltage', label: 'Voltage(1/10)'},
  {key: 'spaPump', label: 'SpaPump'},
  {key: 'orp', label: 'ORP'},
  {key: 'ph', label: 'PH'},
];
const SERIES_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4'];
const SERIES_DARK = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181'];
const SPA_PUMP_ON = 575;
const SPA_PUMP_OFF = 550;

const toPoint = (row: any) => ({
  timestamp: new Date(row.timestamp).getTime(),
  current: row.sbI1 != null ? Math.trunc(row.sbI1 / 3) : undefined,
  voltage: row.sbVout != null ? Math.trunc(row.sbVout / 10) : undefined,
  // Classic plots the spa's pump 1 on low ("SpaPump1Low").
  spaPump: row.P1 != null ? (row.P1 === PumpState.Low ? SPA_PUMP_ON : SPA_PUMP_OFF) : undefined,
  orp: row.sbORP,
  ph: row.sbpH,
});

const CustomTooltip: React.FC<{ active?: any, payload?: any, label?: any }> = ({ active, payload, label }) => {
  const formatTime = useReportTimeFormat();
  if (!active || !payload?.length) return null;
  return (
    <div className="rp-tooltip">
      <span className="rp-tooltip-time">{formatTime(label)}</span>
      {payload.map((el: any, i: number) => (
        <div key={i} className="rp-tooltip-row">
          <span className="rp-swatch" style={{background: el.color}} aria-hidden="true"/>
          <span className="rp-tooltip-name">{el.name}</span>
          <span className="rp-tooltip-value">{el.value}</span>
        </div>
      ))}
    </div>
  );
};

const V1SpaBoyGraph: React.FC<{ spa_guid: string; startDate: string; endDate: string }> = ({spa_guid, startDate, endDate}) => {
  const dispatch: AppDispatch = useDispatch();
  const {data: live, loading, lastQuery} = useSelector((state: RootState) => state.spaHistory);
  const formatTime = useReportTimeFormat();
  const palette = useIsDarkTheme() ? SERIES_DARK : SERIES_LIGHT;

  useEffect(() => {
    if (!spa_guid) return;
    const spaInfo = {guid: spa_guid, date_from: new Date(startDate).getTime(), date_to: new Date(endDate).getTime(), keys: V1_SPABOY_GRAPH_FIELDS, type: 'live'};

    // Already holding this exact report, e.g. coming back from the table view.
    if (lastQuery === JSON.stringify(spaInfo) && (live.length > 0 || loading)) return;

    dispatch(clearSpaHistory());
    dispatch(fetchSpaHistory(spaInfo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, spa_guid, startDate, endDate]);

  const data = useMemo(
    () => [...live].map(toPoint).sort((a, b) => a.timestamp - b.timestamp),
    [live],
  );

  return (
    <div className="rp-graph">
      {loading ?
        <ReportLoadingProgress/>
      : data.length > 0 ?
        <div className="rp-graph-frame rp-graph-titled">
          <div className="rp-graph-title">SMART {palName} PH &amp; ORP Performance vs Time</div>
          <div className="rp-graph-plot">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 16, right: 24, left: 24, bottom: 8 }}>
              <CartesianGrid vertical={false}/>
              <XAxis
                dataKey="timestamp"
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(timestamp) => formatTime(timestamp)}
                scale="time"
              />
              <YAxis axisLine={false} tickLine={false} width={44}/>
              <Tooltip content={<CustomTooltip/>}/>
              <Legend
                verticalAlign="top"
                height={36}
                iconType="plainline"
                formatter={(value) => <span className="rp-legend-text">{value}</span>}
              />
              {SERIES.map((series, i) =>
                <Line
                  key={series.key}
                  isAnimationActive={data.length <= 500}
                  connectNulls={true}
                  type={series.key === 'spaPump' ? 'stepAfter' : 'linear'}
                  name={series.label}
                  dataKey={series.key}
                  stroke={palette[i]}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{r: 4, strokeWidth: 2}}
                />
              )}
              <Brush dataKey="timestamp" height={28} travellerWidth={8} tickFormatter={() => ''}/>
            </LineChart>
          </ResponsiveContainer>
          </div>
        </div>
      :
        <div className="dt-state"><div className="dt-empty-title">No Data Found</div></div>
      }
    </div>
  );
};

export default V1SpaBoyGraph;
